// src/services/demoTracker.js
//
// The public home-page demo ("Learn More About Our Tools").
//
//   loadPublicDemo()     the topics an admin published from Layout Builder ▸
//                        🏠 Home page demo (publicDemo/home — readable by
//                        anyone, written only by admins). null when nothing
//                        is published yet, and the page uses its built-in
//                        sample topics instead.
//   questionsFor(topic)  the topic's game questions: the published ones, or
//                        the static quiz library for the built-in topics.
//   trackDemo(event, {slug, title, n})
//                        counts a click / play for the admin tracker. Events
//                        are queued and sent a few at a time to the trackDemo
//                        Cloud Function, which does all the counting with
//                        admin rights — visitors can't write any counter
//                        themselves.
//
// A visitor is identified only by a random id kept in this browser
// (localStorage "sbl-demo-visitor"). No name, email or IP is stored.

import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

/* ------------------------------------------------------------------ */
/* Published demo content                                              */
/* ------------------------------------------------------------------ */

export const loadPublicDemo = async () => {
  try {
    const snap = await getDoc(doc(db, "publicDemo", "home"));
    if (!snap.exists()) return null;
    const topics = Array.isArray(snap.data().topics) ? snap.data().topics : [];
    return topics.length ? { ...snap.data(), topics } : null;
  } catch (err) {
    // rules not deployed yet, offline — the built-in sample topics still work
    console.warn("[demo] publicDemo/home unavailable:", err?.code || err?.message);
    return null;
  }
};

export const questionsFor = async (topic) => {
  if (Array.isArray(topic?.questions) && topic.questions.length) {
    return { questions: topic.questions, gameType: topic.gameType || "multipleChoice", placeholderImage: topic.placeholderImage || "" };
  }
  if (!topic?.quizSlug) return { questions: [], gameType: topic?.gameType || "" };
  try {
    const { getQuizBySlug } = await import("../data/quiz-library");
    const quiz = getQuizBySlug(topic.quizSlug);
    return {
      questions: quiz?.questions || [],
      gameType: topic.gameType || quiz?.gameType || "multipleChoice",
      placeholderImage: quiz?.placeholderImage || "",
    };
  } catch (err) {
    console.warn("[demo] quiz library unavailable:", err?.message || err);
    return { questions: [], gameType: "" };
  }
};

/* ------------------------------------------------------------------ */
/* Tracking                                                            */
/* ------------------------------------------------------------------ */

const KEY = "sbl-demo-visitor";
let memoryId = "";

export const visitorId = () => {
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    if (!memoryId) memoryId = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    return memoryId;
  }
};

// Your own testing on localhost shouldn't count as visitors.
const isDev = () => {
  try {
    return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  } catch {
    return false;
  }
};

let queue = [];
let timer = null;

const flush = () => {
  clearTimeout(timer);
  timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, 25);
  httpsCallable(functions, "trackDemo")({ visitorId: visitorId(), events }).catch((err) => {
    // never shown to the visitor — the demo works the same either way
    console.warn("[demo] tracking skipped:", err?.code || err?.message);
  });
  if (queue.length) timer = setTimeout(flush, 1500);
};

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

/**
 * event: view | video | video_play | music | music_play | singalong |
 *        singalong_play | game | game_finish | avatar | avatar_save |
 *        classmates | student | leaderboard | controls | custom | signup
 */
export const trackDemo = (event, { slug = "", title = "", n = 0 } = {}) => {
  if (isDev()) return;
  queue.push({
    event: String(event).slice(0, 24),
    slug: String(slug || "").slice(0, 60),
    title: String(title || "").slice(0, 80),
    n: Number.isFinite(Number(n)) ? Math.max(0, Math.min(100000, Math.round(Number(n)))) : 0,
    at: Date.now(),
  });
  clearTimeout(timer);
  timer = setTimeout(flush, queue.length >= 10 ? 0 : 2500);
};
